<template>
  <div class="event-list-container collapsible-section">
    <h3 @click="toggleOpen" class="collapsible-header">
      Evenimente Disponibile
      <span>{{ isOpen ? '▲' : '▼' }}</span>
    </h3>

    <div v-if="isOpen" class="collapsible-content">
      <div v-if="loading">Se încarcă evenimentele...</div>
      
      <div v-if="error" class="error">{{ error }}</div>
      
      <ul v-if="availableEvents.length > 0" class="event-list">
        <li v-for="event in availableEvents" :key="event.id" class="event-item">
          <h4>{{ event.title }}</h4>
          <p>{{ event.description }}</p>
          <p><strong>Locație:</strong> {{ event.location }}</p>
          <p><strong>Începe:</strong> {{ formatData(event.start_time) }}</p>
          <button @click="handleAttend(event.id)" :disabled="attendMessage[event.id]">
            {{ attendMessage[event.id] || 'Înscrie-te' }}
          </button>
        </li>
      </ul>
      
      <p v-if="!loading && availableEvents.length === 0">Nu există evenimente disponibile momentan.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'; // 1. Am scos defineEmits, defineExpose, defineProps
import apiClient from '@/api.js';

// 2. Am scos 'emit' și 'props'. Nu mai sunt necesare.

// 3. 'isOpen' rămâne, dar îl setăm pe 'true' by default, 
//    deoarece acum este o pagină principală.
const isOpen = ref(true); 
const toggleOpen = () => { isOpen.value = !isOpen.value; };

// 4. Stările componentei
const allEvents = ref([]);
const enrolledIds = ref([]); // 5. NOU: O listă locală pentru ID-urile la care e înscris user-ul
const loading = ref(true);
const error = ref('');
const attendMessage = ref({});

// 6. 'computed' se bazează acum pe 'enrolledIds.value' (variabila locală)
const availableEvents = computed(() => {
  return allEvents.value.filter(event => !enrolledIds.value.includes(event.id));
});

const formatData = (isoString) => {
  return new Date(isoString).toLocaleString('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

// 7. Funcția de fetch pentru evenimentele publice (rămâne aproape la fel)
const fetchPublicEvents = async () => {
  try {
    const response = await apiClient.get('/events');
    allEvents.value = response.data;
  } catch (err) {
    console.error("Eroare la preluarea evenimentelor:", err);
    throw err; // Aruncăm eroarea ca să o prindă Promise.all
  }
};

// 8. NOU: Funcție de fetch pentru evenimentele MELE
const fetchMyEvents = async () => {
  try {
    const response = await apiClient.get('/profile/my-events');
    // Extragem doar ID-urile din răspunsul API-ului
    enrolledIds.value = response.data.map(event => event.id);
  } catch (err) {
    console.error("Eroare la preluarea evenimentelor mele:", err);
    throw err; // Aruncăm eroarea
  }
};

// 9. NOU: 'onMounted' folosește Promise.all pentru a rula ambele funcții
onMounted(async () => {
  loading.value = true;
  error.value = '';
  try {
    // Așteptăm ca AMBELE cereri API să se termine
    await Promise.all([
      fetchPublicEvents(),
      fetchMyEvents()
    ]);
  } catch (err) {
    error.value = 'Nu am putut încărca datele. Reîmprospătează pagina.';
  } finally {
    loading.value = false;
  }
});

// 10. Am scos defineExpose. Componenta se descurcă singură.

// 11. 'handleAttend' actualizat
const handleAttend = async (eventId) => {
  try {
    attendMessage.value[eventId] = 'Se procesează...';
    const response = await apiClient.post(`/events/${eventId}/attend`);
    attendMessage.value[eventId] = response.data.message;

    if (response.data.message === 'Înscriere reușită!') {
      // 12. NOU: În loc de 'emit', reîmprospătăm lista de ID-uri înscrise.
      // Asta va face evenimentul să dispară automat din 'availableEvents' (datorită 'computed')
      await fetchMyEvents();
    }
  } catch (err) {
    console.error("Eroare la înscriere:", err);
    attendMessage.value[eventId] = 'Eroare!';
  }
};
</script>

<style scoped>
/* Stilurile rămân neschimbate */
.event-list-container {
  margin-top: 30px;
}
.collapsible-header {
  cursor: pointer; display: flex; justify-content: space-between;
  align-items: center; border-bottom: 2px solid #6c757d;
  padding-bottom: 10px; margin: 0;
}
.collapsible-header:hover { background-color: #f8f9fa; }
.collapsible-header span { font-size: 0.8em; }
.collapsible-content { padding-top: 20px; }
.event-list { list-style-type: none; padding: 0; }
.event-item {
  border: 1px solid #eee; padding: 15px;
  margin-bottom: 15px; border-radius: 8px;
}
.event-item h4 { margin-top: 0; }
.event-item button {
  background-color: #28a745; color: white;
  padding: 8px 12px; border: none;
  border-radius: 4px; cursor: pointer;
}
.event-item button:hover { background-color: #218838; }
.event-item button:disabled { background-color: #ccc; cursor: not-allowed; }
.error { color: red; }
</style>